import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Plus, Sunrise, Sunset, Coffee, Trash2, Edit2, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

// Three-list checklist builder for the Operations page. Managers curate
// standing tasks under Opening / Closing / Downtime. When staff clock in
// at `/clock`, the backend materialises the active items into today's
// task list and balances them across whoever is on the clock.
const LISTS = [
  { key: "opening",  label: "Opening",  Icon: Sunrise, blurb: "Runs first each day — assigned as soon as the first person clocks in." },
  { key: "downtime", label: "Downtime", Icon: Coffee,  blurb: "Between-client tasks. Pick these up when there's a gap." },
  { key: "closing",  label: "Closing",  Icon: Sunset,  blurb: "End of day — kept in the pool until the last person clocks out." },
];

const DAYS = [
  { i: 0, s: "M", full: "Mon" },
  { i: 1, s: "T", full: "Tue" },
  { i: 2, s: "W", full: "Wed" },
  { i: 3, s: "T", full: "Thu" },
  { i: 4, s: "F", full: "Fri" },
  { i: 5, s: "S", full: "Sat" },
  { i: 6, s: "S", full: "Sun" },
];

const summariseDays = (active_days) => {
  if (!active_days || active_days.length === 0 || active_days.length === 7) return "Every day";
  const set = new Set(active_days);
  const weekdays = [0, 1, 2, 3, 4].every(d => set.has(d)) && !set.has(5) && !set.has(6);
  const weekends = set.has(5) && set.has(6) && ![0, 1, 2, 3, 4].some(d => set.has(d));
  if (weekdays) return "Weekdays";
  if (weekends) return "Weekends";
  return active_days.sort((a, b) => a - b).map(d => DAYS[d].full).join(", ");
};

export default function ChecklistsTab() {
  const [active, setActive] = useState("opening");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rebalancing, setRebalancing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/checklists");
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load checklists");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rebalance = async () => {
    setRebalancing(true);
    try {
      const res = await api.post("/checklists/rebalance");
      toast.success(`Rebalanced — ${res.data.assigned} tasks across ${res.data.workers} on-shift staff`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't rebalance");
    }
    setRebalancing(false);
  };

  const filtered = items.filter(i => i.list_type === active);
  const activeMeta = LISTS.find(l => l.key === active);
  const ActiveIcon = activeMeta.Icon;

  return (
    <div className="space-y-6" data-testid="checklists-tab">
      {/* Header actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-stone-600 font-['Manrope']">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span><span className="font-semibold text-[#072032]">Repeats daily</span> — regenerates every morning + auto-assigns whenever a staffer clocks in or out.</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={rebalance} disabled={rebalancing}
            data-testid="checklist-rebalance-btn"
            className="border-stone-200 rounded-full h-9 px-4 text-xs">
            {rebalancing ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Balancing…</> : <><RefreshCw className="w-3 h-3 mr-1.5" />Rebalance now</>}
          </Button>
          <Link to="/tasks-board" data-testid="checklist-open-board-link">
            <Button variant="outline" size="sm" className="border-stone-200 rounded-full h-9 px-4 text-xs">
              <ExternalLink className="w-3 h-3 mr-1.5" />Open live board
            </Button>
          </Link>
        </div>
      </div>

      {/* List-type switcher */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="checklist-type-switcher">
        {LISTS.map(({ key, label, Icon, blurb }) => {
          const count = items.filter(i => i.list_type === key && i.is_active !== false).length;
          const isActive = active === key;
          return (
            <button key={key} onClick={() => setActive(key)}
              data-testid={`checklist-type-${key}`}
              className={`text-left p-4 rounded-2xl border transition-all ${
                isActive
                  ? "border-[#072032] bg-[#072032] text-white shadow-md"
                  : "border-stone-200 bg-white hover:border-stone-300 text-stone-800"
              }`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-stone-500"}`} strokeWidth={1.6} />
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  isActive ? "bg-white/15 text-white" : "bg-stone-100 text-stone-600"
                }`}>
                  {count} active
                </span>
              </div>
              <p className="font-['Outfit'] text-lg font-semibold">{label}</p>
              <p className={`text-xs mt-1 ${isActive ? "text-white/70" : "text-stone-500"} font-['Manrope']`}>{blurb}</p>
            </button>
          );
        })}
      </div>

      {/* Items list */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="checklist-items-panel">
        <div className="p-4 border-b border-stone-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ActiveIcon className="w-4 h-4 text-stone-400" strokeWidth={1.6} />
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#072032]">{activeMeta.label} tasks</h3>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setShowModal(true); }}
            data-testid="checklist-add-btn"
            className="rounded-full h-9 px-4 text-xs bg-[#072032] text-white hover:bg-[#0a2b40]">
            <Plus className="w-3 h-3 mr-1.5" />Add task
          </Button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center text-stone-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-stone-500 font-['Manrope']" data-testid="checklist-empty">
            <p className="mb-1 text-stone-700 font-semibold font-['Outfit']">No {activeMeta.label.toLowerCase()} tasks yet</p>
            <p>Add the first one to auto-assign it at the next clock-in.</p>
          </div>
        ) : (
          <ul className="divide-y divide-stone-100" data-testid="checklist-items-list">
            {filtered.map(item => (
              <li key={item.item_id}
                data-testid={`checklist-item-${item.item_id}`}
                className="p-4 flex items-center gap-3 group hover:bg-stone-50/60">
                <div className={`w-1.5 h-10 rounded-full ${item.is_active === false ? "bg-stone-200" : "bg-emerald-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold font-['Outfit'] ${item.is_active === false ? "text-stone-400 line-through" : "text-[#072032]"}`}>{item.title}</p>
                  {item.description && (
                    <p className="text-xs text-stone-500 font-['Manrope'] mt-0.5 line-clamp-2">{item.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-[11px] text-stone-400 font-['Manrope'] mt-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 font-semibold">
                      {summariseDays(item.active_days)}
                    </span>
                    {item.estimated_minutes ? <span>~{item.estimated_minutes} min</span> : null}
                    {item.weight >= 2 ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${item.weight === 3 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`} data-testid={`checklist-weight-${item.item_id}`}>
                        {item.weight === 3 ? "Heavy" : "Medium"}
                      </span>
                    ) : null}
                    {item.is_active === false ? <span className="text-amber-600">Paused</span> : null}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setEditing(item); setShowModal(true); }}
                  data-testid={`checklist-edit-${item.item_id}`}
                  className="h-8 w-8 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={async () => {
                  if (!window.confirm(`Delete "${item.title}"? Existing tasks already generated from it stay on today's board.`)) return;
                  try {
                    await api.delete(`/checklists/${item.item_id}`);
                    toast.success("Deleted");
                    load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Couldn't delete");
                  }
                }}
                  data-testid={`checklist-delete-${item.item_id}`}
                  className="h-8 w-8 p-0 rounded-full text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showModal && (
        <ChecklistItemModal
          listType={active}
          item={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ChecklistItemModal({ listType, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: item?.title || "",
    description: item?.description || "",
    estimated_minutes: item?.estimated_minutes || "",
    is_active: item?.is_active !== false,
    list_type: item?.list_type || listType,
    active_days: item?.active_days && item.active_days.length ? item.active_days : [0, 1, 2, 3, 4, 5, 6],
    weight: item?.weight || 1,
  });
  const [saving, setSaving] = useState(false);

  const toggleDay = (i) => {
    setForm(f => {
      const s = new Set(f.active_days);
      if (s.has(i)) s.delete(i); else s.add(i);
      return { ...f, active_days: Array.from(s).sort((a, b) => a - b) };
    });
  };
  const setAllDays = () => setForm(f => ({ ...f, active_days: [0, 1, 2, 3, 4, 5, 6] }));
  const setWeekdays = () => setForm(f => ({ ...f, active_days: [0, 1, 2, 3, 4] }));
  const setWeekends = () => setForm(f => ({ ...f, active_days: [5, 6] }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (form.active_days.length === 0) { toast.error("Pick at least one day"); return; }
    setSaving(true);
    try {
      const payload = {
        list_type: form.list_type,
        title: form.title.trim(),
        description: form.description,
        estimated_minutes: form.estimated_minutes ? Number(form.estimated_minutes) : null,
        is_active: form.is_active,
        active_days: form.active_days,
        weight: form.weight,
      };
      if (item) {
        await api.put(`/checklists/${item.item_id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/checklists", payload);
        toast.success("Added");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="checklist-item-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{item ? "Edit task" : `Add ${listType} task`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 font-['Manrope']">List</label>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {LISTS.map(l => (
                <button key={l.key}
                  data-testid={`checklist-modal-type-${l.key}`}
                  onClick={() => setForm(f => ({ ...f, list_type: l.key }))}
                  className={`h-9 rounded-full text-xs font-semibold border transition-all ${
                    form.list_type === l.key
                      ? "bg-[#072032] text-white border-[#072032]"
                      : "border-stone-200 text-stone-600 hover:border-stone-300"
                  }`}>{l.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 font-['Manrope']">Title</label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="checklist-modal-title"
              placeholder="e.g. Sweep floors" className="mt-1.5 h-10" autoFocus />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 font-['Manrope']">Description</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              data-testid="checklist-modal-desc"
              placeholder="Optional detail — how to do it, where the supplies are, etc."
              className="mt-1.5 min-h-[80px]" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 font-['Manrope']">Estimated minutes</label>
            <Input type="number" min="0" value={form.estimated_minutes}
              onChange={e => setForm(f => ({ ...f, estimated_minutes: e.target.value }))}
              data-testid="checklist-modal-mins"
              placeholder="5" className="mt-1.5 h-10 w-32" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 font-['Manrope']">Effort</label>
            <p className="text-[11px] text-stone-500 font-['Manrope'] mb-1.5">Heavier chores count for more, so the daily rotation balances effort — not just how many tasks each person has.</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="checklist-modal-weight">
              {[{ v: 1, l: "Light" }, { v: 2, l: "Medium" }, { v: 3, l: "Heavy" }].map(w => (
                <button key={w.v} type="button"
                  data-testid={`checklist-modal-weight-${w.v}`}
                  onClick={() => setForm(f => ({ ...f, weight: w.v }))}
                  className={`h-9 rounded-full text-xs font-semibold border transition-all ${
                    form.weight === w.v ? "bg-[#072032] text-white border-[#072032]" : "border-stone-200 text-stone-600 hover:border-stone-300"
                  }`}>{w.l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 font-['Manrope']">Runs on</label>
              <div className="flex gap-1">
                <button type="button" onClick={setAllDays} data-testid="checklist-days-all"
                  className="text-[10px] font-bold uppercase tracking-widest text-stone-500 hover:text-[#072032] px-2 py-0.5 rounded-full hover:bg-stone-100">Every day</button>
                <button type="button" onClick={setWeekdays} data-testid="checklist-days-weekdays"
                  className="text-[10px] font-bold uppercase tracking-widest text-stone-500 hover:text-[#072032] px-2 py-0.5 rounded-full hover:bg-stone-100">Weekdays</button>
                <button type="button" onClick={setWeekends} data-testid="checklist-days-weekends"
                  className="text-[10px] font-bold uppercase tracking-widest text-stone-500 hover:text-[#072032] px-2 py-0.5 rounded-full hover:bg-stone-100">Weekends</button>
              </div>
            </div>
            <div className="flex gap-1.5" data-testid="checklist-modal-days">
              {DAYS.map(({ i, s, full }) => {
                const on = form.active_days.includes(i);
                return (
                  <button key={i} type="button" onClick={() => toggleDay(i)}
                    data-testid={`checklist-modal-day-${i}`}
                    title={full}
                    className={`w-9 h-9 rounded-full text-xs font-bold transition-all border ${
                      on
                        ? "bg-[#072032] text-white border-[#072032] shadow-sm"
                        : "bg-white text-stone-400 border-stone-200 hover:border-stone-300"
                    }`}>{s}</button>
                );
              })}
            </div>
            <p className="text-[11px] text-stone-500 font-['Manrope'] mt-1.5">
              Task regenerates on each selected day and gets balanced across whoever is on the clock.
            </p>
          </div>
          <div className="flex items-center justify-between bg-stone-50 rounded-xl p-3">
            <div>
              <p className="text-sm font-semibold text-stone-800 font-['Outfit']">Active</p>
              <p className="text-xs text-stone-500 font-['Manrope']">Paused tasks don&apos;t generate at clock-in.</p>
            </div>
            <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
              data-testid="checklist-modal-active" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
          <Button variant="outline" onClick={onClose} className="rounded-full h-10 px-5 text-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="checklist-modal-save"
            className="rounded-full h-10 px-5 text-sm bg-[#072032] text-white hover:bg-[#0a2b40]">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</> : (item ? "Save" : "Add task")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
